import { FC, useState, useRef, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Portal,
} from "@chakra-ui/react";

// Playlist of royalty-free retro/chiptune-style tracks
const playlist = [
  {
    title: "Retro Vibes",
    artist: "SoundHelix",
    src: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3",
  },
  {
    title: "8-Bit Dreams",
    artist: "SoundHelix",
    src: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-2.mp3",
  },
  {
    title: "Pixel Party",
    artist: "SoundHelix",
    src: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-3.mp3",
  },
  {
    title: "Chiptune Chill",
    artist: "SoundHelix",
    src: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-4.mp3",
  },
];

interface RetroMusicPlayerProps {
  isVisible: boolean;
  onToggle: () => void;
}

export const RetroMusicPlayer: FC<RetroMusicPlayerProps> = ({
  isVisible,
  onToggle,
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(0);
  const [volume, setVolume] = useState(50);
  const [isMuted, setIsMuted] = useState(true); // Start muted for autoplay policy
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showPlaylist, setShowPlaylist] = useState(false);
  const [visualizerBars, setVisualizerBars] = useState([
    3, 5, 2, 7, 4, 6, 3, 5,
  ]);

  // Update visualizer bars when playing
  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      setVisualizerBars(
        Array(8)
          .fill(0)
          .map(() => Math.floor(Math.random() * 8) + 1),
      );
    }, 150);

    return () => clearInterval(interval);
  }, [isPlaying]);

  // Update current time
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => playNext();

    audio.addEventListener("timeupdate", updateTime);
    audio.addEventListener("loadedmetadata", updateDuration);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", updateTime);
      audio.removeEventListener("loadedmetadata", updateDuration);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [currentTrack]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(console.error);
    }
    setIsPlaying(!isPlaying);
  };

  const stop = () => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.pause();
    audio.currentTime = 0;
    setIsPlaying(false);
  };

  const playPrev = () => {
    setCurrentTrack((prev) => (prev === 0 ? playlist.length - 1 : prev - 1));
    setIsPlaying(true);
  };

  const playNext = () => {
    setCurrentTrack((prev) => (prev === playlist.length - 1 ? 0 : prev + 1));
    setIsPlaying(true);
  };

  const handleVolumeChange = (val: number) => {
    setVolume(val);
    if (audioRef.current) {
      audioRef.current.volume = val / 100;
    }
    if (val > 0) setIsMuted(false);
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
    }
    setIsMuted(!isMuted);
  };

  const handleSeek = (val: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = val;
    }
    setCurrentTime(val);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Auto-play when track changes
  useEffect(() => {
    if (isPlaying && audioRef.current) {
      audioRef.current.play().catch(console.error);
    }
  }, [currentTrack, isPlaying]);

  if (!isVisible) return null;

  return (
    <Portal>
      {/* 
        WINAMP PLAYER - Positioned at bottom right, above the taskbar
        cursor: default ensures normal pointer inside the player for easy interaction
      */}
      <Box
        position="fixed"
        bottom="48px" /* Above the 40px taskbar + 8px margin */
        right="8px" /* Aligned near the system tray */
        zIndex={1500}
        cursor="default" /* Normal cursor inside the player */
        sx={{
          "& *": {
            cursor:
              "default !important" /* Override custom cursor for all children */,
          },
          "& button, & [role='button'], & input, & .slider-thumb": {
            cursor:
              "pointer !important" /* But keep pointer for interactive elements */,
          },
        }}
      >
        <Box
          bg="#232323"
          border="2px solid #000"
          boxShadow="inset 1px 1px 0 #4a4a4a, inset -1px -1px 0 #000, 4px 4px 0px #000"
          w="280px"
          userSelect="none"
        >
          {/* Title Bar */}
          <Flex
            bg="linear-gradient(180deg, #4a6ea5 0%, #3a5a8a 50%, #2a4a7a 100%)"
            px={2}
            py={1}
            justify="space-between"
            align="center"
          >
            <Flex align="center" gap={1}>
              <Text fontSize="xs" color="#00ff00" fontWeight="bold">
                ▶
              </Text>
              <Text
                fontSize="xs"
                color="white"
                fontFamily="'Tahoma', sans-serif"
                fontWeight="bold"
              >
                Winamp
              </Text>
            </Flex>
            <Flex gap={1}>
              <Box
                w="14px"
                h="14px"
                bg="linear-gradient(180deg, #c0c0c0, #808080)"
                border="1px outset #fff"
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontSize="10px"
                cursor="pointer"
                onClick={onToggle}
                _hover={{ bg: "#a0a0a0" }}
              >
                _
              </Box>
              <Box
                w="14px"
                h="14px"
                bg="linear-gradient(180deg, #c0c0c0, #808080)"
                border="1px outset #fff"
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontSize="10px"
                cursor="pointer"
                onClick={onToggle}
                _hover={{ bg: "#ff0000", color: "white" }}
              >
                ×
              </Box>
            </Flex>
          </Flex>

          {/* Main Display */}
          <Box bg="#000" p={2}>
            {/* LCD Display */}
            <Box
              bg="#000"
              border="1px inset #333"
              p={2}
              mb={2}
              position="relative"
            >
              {/* Mute Warning */}
              {isMuted && (
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bg="#ff0000"
                  color="white"
                  fontSize="8px"
                  textAlign="center"
                  fontFamily="'Tahoma', sans-serif"
                  py={0.5}
                  cursor="pointer"
                  onClick={toggleMute}
                >
                  🔇 MUTED - Click to unmute
                </Box>
              )}

              {/* Visualizer */}
              <Flex
                h="20px"
                align="flex-end"
                gap="2px"
                mb={2}
                mt={isMuted ? 3 : 0}
              >
                {visualizerBars.map((height, i) => (
                  <Box
                    key={i}
                    w="8px"
                    h={isPlaying ? `${height * 2}px` : "2px"}
                    bg={
                      isPlaying
                        ? `linear-gradient(180deg, #00ff00 0%, #008800 100%)`
                        : "#004400"
                    }
                    transition="height 0.1s"
                  />
                ))}
              </Flex>

              {/* Track Info */}
              <Text
                color="#00ff00"
                fontSize="10px"
                fontFamily="'VT323', monospace"
                noOfLines={1}
                textShadow="0 0 5px #00ff00"
              >
                {playlist[currentTrack].title}
              </Text>
              <Text
                color="#00aa00"
                fontSize="9px"
                fontFamily="'VT323', monospace"
              >
                {playlist[currentTrack].artist}
              </Text>

              {/* Time Display */}
              <Flex justify="space-between" mt={1}>
                <Text
                  color="#00ff00"
                  fontSize="9px"
                  fontFamily="'VT323', monospace"
                >
                  {formatTime(currentTime)}
                </Text>
                <Text
                  color="#00ff00"
                  fontSize="9px"
                  fontFamily="'VT323', monospace"
                >
                  {formatTime(duration)}
                </Text>
              </Flex>

              {/* Seek Bar */}
              <Slider
                value={currentTime}
                min={0}
                max={duration || 100}
                onChange={handleSeek}
                size="sm"
                mt={1}
              >
                <SliderTrack bg="#333" h="4px">
                  <SliderFilledTrack bg="#00ff00" />
                </SliderTrack>
                <SliderThumb
                  boxSize={2}
                  bg="#00ff00"
                  className="slider-thumb"
                />
              </Slider>
            </Box>

            {/* Controls */}
            <Flex justify="center" gap={1} mb={2}>
              {/* Previous */}
              <Box
                as="button"
                w="28px"
                h="18px"
                bg="linear-gradient(180deg, #4a4a4a, #2a2a2a)"
                border="1px outset #666"
                color="#00ff00"
                fontSize="10px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                _hover={{ bg: "#3a3a3a" }}
                _active={{ border: "1px inset #333" }}
                onClick={playPrev}
              >
                ⏮
              </Box>
              {/* Play/Pause */}
              <Box
                as="button"
                w="28px"
                h="18px"
                bg="linear-gradient(180deg, #4a4a4a, #2a2a2a)"
                border="1px outset #666"
                color="#00ff00"
                fontSize="10px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                _hover={{ bg: "#3a3a3a" }}
                _active={{ border: "1px inset #333" }}
                onClick={togglePlay}
              >
                {isPlaying ? "⏸" : "▶"}
              </Box>
              {/* Stop */}
              <Box
                as="button"
                w="28px"
                h="18px"
                bg="linear-gradient(180deg, #4a4a4a, #2a2a2a)"
                border="1px outset #666"
                color="#00ff00"
                fontSize="10px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                _hover={{ bg: "#3a3a3a" }}
                _active={{ border: "1px inset #333" }}
                onClick={stop}
              >
                ⏹
              </Box>
              {/* Next */}
              <Box
                as="button"
                w="28px"
                h="18px"
                bg="linear-gradient(180deg, #4a4a4a, #2a2a2a)"
                border="1px outset #666"
                color="#00ff00"
                fontSize="10px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                _hover={{ bg: "#3a3a3a" }}
                _active={{ border: "1px inset #333" }}
                onClick={playNext}
              >
                ⏭
              </Box>
              {/* Playlist Toggle */}
              <Box
                as="button"
                w="28px"
                h="18px"
                bg={
                  showPlaylist
                    ? "linear-gradient(180deg, #005500, #003300)"
                    : "linear-gradient(180deg, #4a4a4a, #2a2a2a)"
                }
                border="1px outset #666"
                color="#00ff00"
                fontSize="10px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                _hover={{ bg: "#3a3a3a" }}
                _active={{ border: "1px inset #333" }}
                onClick={() => setShowPlaylist(!showPlaylist)}
              >
                📋
              </Box>
            </Flex>

            {/* Volume Control */}
            <Flex align="center" gap={2} px={2}>
              <Box
                as="button"
                fontSize="12px"
                onClick={toggleMute}
                color={isMuted ? "#ff0000" : "#00ff00"}
              >
                {isMuted ? "🔇" : "🔊"}
              </Box>
              <Slider
                value={isMuted ? 0 : volume}
                min={0}
                max={100}
                onChange={handleVolumeChange}
                size="sm"
                flex={1}
              >
                <SliderTrack bg="#333" h="4px">
                  <SliderFilledTrack bg={isMuted ? "#666" : "#00ff00"} />
                </SliderTrack>
                <SliderThumb
                  boxSize={2}
                  bg={isMuted ? "#666" : "#00ff00"}
                  className="slider-thumb"
                />
              </Slider>
              <Text
                color="#00ff00"
                fontSize="9px"
                fontFamily="'VT323', monospace"
                minW="25px"
                textAlign="right"
              >
                {isMuted ? "0" : volume}%
              </Text>
            </Flex>
          </Box>

          {/* Playlist Panel */}
          {showPlaylist && (
            <Box
              bg="#1a1a1a"
              borderTop="1px solid #333"
              maxH="120px"
              overflowY="auto"
            >
              {playlist.map((track, index) => (
                <Flex
                  key={index}
                  px={2}
                  py={1}
                  bg={currentTrack === index ? "#003300" : "transparent"}
                  color={currentTrack === index ? "#00ff00" : "#00aa00"}
                  fontSize="10px"
                  fontFamily="'VT323', monospace"
                  cursor="pointer"
                  _hover={{ bg: "#002200" }}
                  onClick={() => {
                    setCurrentTrack(index);
                    setIsPlaying(true);
                  }}
                >
                  <Text flex={1} noOfLines={1}>
                    {index + 1}. {track.title} - {track.artist}
                  </Text>
                  {currentTrack === index && isPlaying && (
                    <Text color="#00ff00">▶</Text>
                  )}
                </Flex>
              ))}
            </Box>
          )}

          {/* Status Bar */}
          <Flex
            bg="#2a2a2a"
            px={2}
            py={0.5}
            borderTop="1px solid #4a4a4a"
            justify="space-between"
          >
            <Text color="#888" fontSize="8px" fontFamily="'Tahoma', sans-serif">
              {playlist.length} tracks
            </Text>
            <Text color="#888" fontSize="8px" fontFamily="'Tahoma', sans-serif">
              Winamp 2.95
            </Text>
          </Flex>
        </Box>

        {/* Hidden Audio Element */}
        <audio
          ref={audioRef}
          src={playlist[currentTrack].src}
          muted={isMuted}
        />
      </Box>
    </Portal>
  );
};
